import { useEffect, useMemo, useState } from "react";
import { fetchRequests, fetchOrgYears, type TimeOffRequest } from "../lib/api";
import { TIME_OFF_TYPES, type TimeOffType } from "../lib/shared/timeOffTypes";
import RequestTable from "../components/RequestTable";
import { parseDateOnly } from "../lib/dateOnly";
import { requestsToCsv, downloadCsv } from "../lib/csv";
import { requestDurationDays } from "../lib/requestDuration";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Executive() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [requests, setRequests] = useState<TimeOffRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<TimeOffType | null>(null);

  useEffect(() => {
    fetchOrgYears()
      .then(setAvailableYears)
      .catch(() => setAvailableYears([new Date().getFullYear()]));
  }, []);

  useEffect(() => {
    setRequests(null);
    setSelectedType(null);
    fetchRequests("org", year)
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load requests"));
  }, [year]);

  const approved = useMemo(() => requests?.filter((r) => r.status === "approved") ?? [], [requests]);

  const byType = useMemo(() => {
    const counts: Record<TimeOffType, number> = { vacation: 0, sick: 0, personal: 0 };
    for (const r of approved) counts[r.type]++;
    return counts;
  }, [approved]);

  // Full Jan-Dec grid for the selected year (rather than "whichever months
  // happen to appear"), so a quiet month reads as 0, not "missing".
  const byMonth = useMemo(() => {
    const counts = new Array(12).fill(0);
    for (const r of approved) {
      const d = parseDateOnly(r.start_date);
      if (d.getFullYear() === year) counts[d.getMonth()]++;
    }
    return counts;
  }, [approved, year]);

  const maxMonthCount = Math.max(1, ...byMonth);

  // YTD approved days off per person, split by type, for the By person chart.
  // Days (not request counts) so a two-week vacation reads as bigger than a
  // single sick day -- weekends don't count, half-days count as 0.5.
  const byPerson = useMemo(() => {
    const totals = new Map<
      string,
      { email: string; name: string; byType: Record<TimeOffType, number>; total: number }
    >();

    for (const r of approved) {
      const key = r.person_email;
      if (!totals.has(key)) {
        totals.set(key, {
          email: r.person_email,
          name: r.person_name ?? r.person_email,
          byType: { vacation: 0, sick: 0, personal: 0 },
          total: 0,
        });
      }
      const entry = totals.get(key)!;
      const days = requestDurationDays(r.start_date, r.end_date, r.day_part);
      entry.byType[r.type] += days;
      entry.total += days;
    }

    return Array.from(totals.values()).sort((a, b) => b.total - a.total);
  }, [approved]);

  const maxPersonTotal = Math.max(1, ...byPerson.map((p) => p.total));

  const detailRequests = useMemo(
    () => (selectedType ? approved.filter((r) => r.type === selectedType) : []),
    [approved, selectedType]
  );

  function handleDownloadCsv() {
    if (!requests) return;
    downloadCsv(`dispatch-time-off-${year}.csv`, requestsToCsv(requests));
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="di-h3">Org-wide time off</h1>
        <div className="exec-toolbar">
          <select
            className="di-select"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            aria-label="Year"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            className="di-btn di-btn-outline di-btn-small"
            onClick={handleDownloadCsv}
            disabled={!requests || requests.length === 0}
          >
            Download CSV
          </button>
        </div>
      </div>

      {error && <p className="form-error">{error}</p>}

      {requests === null ? (
        <p className="di-small">Loading…</p>
      ) : (
        <>
          <div className="stat-grid">
            {(Object.keys(TIME_OFF_TYPES) as TimeOffType[]).map((key) => {
              const isSelected = selectedType === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`stat-card stat-card-clickable${isSelected ? " stat-card-selected" : ""}`}
                  onClick={() => setSelectedType(isSelected ? null : key)}
                  aria-pressed={isSelected}
                >
                  <p className="di-eyebrow">{TIME_OFF_TYPES[key].label}</p>
                  <p className="stat-number">{byType[key]}</p>
                  <p className="di-small">acknowledged requests · tap for detail</p>
                </button>
              );
            })}
          </div>

          <section className="di-card">
            <h2 className="di-h4">By month · {year}</h2>
            <div className="month-bars">
              {MONTHS_SHORT.map((label, i) => (
                <div className="month-bar-row" key={label}>
                  <span className="di-small month-bar-label">{label}</span>
                  <div className="month-bar-track">
                    <div
                      className="month-bar-fill"
                      style={{ width: `${(byMonth[i] / maxMonthCount) * 100}%` }}
                    />
                  </div>
                  <span className="di-small">{byMonth[i]}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="di-card">
            <h2 className="di-h4">By person · {year}</h2>
            <div className="di-small legend-row">
              {(Object.keys(TIME_OFF_TYPES) as TimeOffType[]).map((key) => (
                <span className="legend-item" key={key}>
                  <span className="legend-swatch" style={{ background: TIME_OFF_TYPES[key].color }} />
                  {TIME_OFF_TYPES[key].label}
                </span>
              ))}
            </div>
            {byPerson.length === 0 ? (
              <p className="di-small">No acknowledged requests in {year}.</p>
            ) : (
              <div className="person-bars">
                {byPerson.map((p) => (
                  <div className="person-bar-row" key={p.email}>
                    <span className="di-small person-bar-label" title={p.email}>
                      {p.name}
                    </span>
                    <div className="person-bar-track">
                      <div className="person-bar-fill" style={{ width: `${(p.total / maxPersonTotal) * 100}%` }}>
                        {(Object.keys(TIME_OFF_TYPES) as TimeOffType[]).map((key) =>
                          p.byType[key] > 0 ? (
                            <div
                              key={key}
                              className="person-bar-segment"
                              style={{
                                width: `${(p.byType[key] / p.total) * 100}%`,
                                background: TIME_OFF_TYPES[key].color,
                              }}
                              title={`${TIME_OFF_TYPES[key].label}: ${p.byType[key]}d`}
                            />
                          ) : null
                        )}
                      </div>
                    </div>
                    <span className="di-small person-bar-total">{p.total}d</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {selectedType && (
            <section className="di-card">
              <h2 className="di-h4">{TIME_OFF_TYPES[selectedType].label} · {year}</h2>
              <RequestTable
                requests={detailRequests}
                showPerson
                emptyMessage={`No acknowledged ${TIME_OFF_TYPES[selectedType].label.toLowerCase()} requests in ${year}.`}
              />
            </section>
          )}

          <section className="di-card">
            <h2 className="di-h4">All requests · {year}</h2>
            <RequestTable
              requests={requests}
              showPerson
              showSubmittedAt
              showAcknowledgedBy
              emptyMessage="No time-off requests yet."
            />
          </section>
        </>
      )}
    </div>
  );
}
